import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import {
  EmojiEmotions,
  Public,
  TrendingUp,
  Security,
  School,
  WorkspacePremium,
  ArrowForward,
  CheckCircle,
  Business,
  Language,
  Shield,
  HeadsetMic,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CTASection from '../components/common/CTASection';

const About = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const values = [
    { icon: <Public />, title: 'Global Mindset', desc: 'Thinking globally, acting locally', color: '#e60108' },
    { icon: <TrendingUp />, title: 'Innovation', desc: 'Pushing boundaries of technology', color: '#191919' },
    { icon: <Security />, title: 'Reliability', desc: 'Trust through consistency', color: '#e60108' },
    { icon: <EmojiEmotions />, title: 'Customer First', desc: 'Your success is our priority', color: '#a0a0a0' },
  ];

  const milestones = [
    { year: '2005', title: 'Company Founded', description: 'Started with a vision to connect the world', color: '#e60108' },
    { year: '2010', title: 'Global Expansion', description: 'Expanded to 50+ countries worldwide', color: '#191919' },
    { year: '2015', title: 'Fiber Network Launch', description: 'Launched 500,000km fiber network', color: '#e60108' },
    { year: '2019', title: 'Data Centers', description: 'Opened 50+ data centers globally', color: '#191919' },
    { year: '2022', title: '5G Ready', description: 'Completed 5G infrastructure rollout', color: '#e60108' },
    { year: '2024', title: 'AI Integration', description: 'Launched AI-powered network management', color: '#191919' },
  ];

  const statsData = [
    { value: '500,000+', label: 'KM of Fiber Network', color: '#e60108' },
    { value: '10,000+', label: 'Telecom Towers', color: '#191919' },
    { value: '150+', label: 'Countries Served', color: '#e60108' },
    { value: '100%', label: 'Uptime Guarantee', color: '#191919' },
  ];

  const benefits = [
    { text: 'Global reach across 150+ countries', icon: <Language sx={{ fontSize: 18 }} /> },
    { text: '99.999% uptime guaranteed', icon: <CheckCircle sx={{ fontSize: 18 }} /> },
    { text: 'ISO 27001 certified security', icon: <Shield sx={{ fontSize: 18 }} /> },
    { text: '24/7 multilingual support', icon: <HeadsetMic sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '60vh',
          minHeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Connecting the World.jpg"
          alt="Connecting the World"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="About Us"
              icon={<Business sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '0.5px',
                mx: 'auto',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Leading the Future of
              <br />
              <Box component="span" sx={{ color: '#e60108' }}>
                Global Communication
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              Established in 2005, we've grown into a global leader in telecommunications, 
              connecting businesses across 150+ countries with cutting-edge infrastructure.
            </Typography>
          </motion.div>
        </Container>
      </Box>
      
      {/* Stats Section */}
      <Box sx={{ py: 8, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} justifyContent="center">
            {statsData.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ textAlign: 'center' }}
                >
                  <Typography 
                    variant="h2" 
                    fontWeight="800" 
                    sx={{ 
                      color: stat.color,
                      fontSize: { xs: '1.8rem', md: '2.5rem' },
                      mb: 1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#a0a0a0' }}>
                    {stat.label}
                  </Typography>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Our Story Section - Centered, No Image */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip 
              label="Our Story" 
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
              Connecting the World <br />
              <Box component="span" sx={{ color: '#e60108' }}>Since 2005</Box>
            </Typography>
            <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 2, lineHeight: 1.8, maxWidth: 800, mx: 'auto' }}>
              Founded with a vision to eliminate communication barriers worldwide, we've grown 
              into a global leader in telecommunications and connectivity solutions.
            </Typography>
            <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 4, lineHeight: 1.8, maxWidth: 800, mx: 'auto' }}>
              With over 500,000 km of fiber optic networks, 10,000+ telecom towers, and 50+ data 
              centers worldwide, we're building the backbone of global digital communication.
            </Typography>
            
            <Stack spacing={2} sx={{ mt: 3, maxWidth: 500, mx: 'auto' }}>
              {benefits.map((item, index) => (
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" key={index}>
                  <Box sx={{ color: '#e60108' }}>{item.icon}</Box>
                  <Typography variant="body2" sx={{ color: '#191919' }}>{item.text}</Typography>
                </Stack>
              ))}
            </Stack>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                variant="contained" 
                endIcon={<ArrowForward />}
                onClick={() => navigate('/contact')}
                sx={{ 
                  mt: 4,
                  bgcolor: '#e60108',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  mx: 'auto',
                  display: 'inline-flex',
                  '&:hover': {
                    bgcolor: '#b80006',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Learn More About Us
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Mission & Vision - Centered, No Images */}
      <Box sx={{ py: 10, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%', p: 4, textAlign: 'center', borderRadius: 4 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: '#e60108',
                      margin: '0 auto 24px',
                    }}
                  >
                    <School sx={{ fontSize: 40, color: '#fefefe' }} />
                  </Avatar>
                  <Typography variant="h4" gutterBottom fontWeight="800" sx={{ color: '#191919' }}>
                    Our Mission
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#a0a0a0', lineHeight: 1.8 }}>
                    To democratize global connectivity by providing accessible, reliable, 
                    and cutting-edge communication solutions that empower businesses and 
                    individuals worldwide.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%', p: 4, textAlign: 'center', borderRadius: 4 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: '#191919',
                      margin: '0 auto 24px',
                    }}
                  >
                    <WorkspacePremium sx={{ fontSize: 40, color: '#fefefe' }} />
                  </Avatar>
                  <Typography variant="h4" gutterBottom fontWeight="800" sx={{ color: '#191919' }}>
                    Our Vision
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#a0a0a0', lineHeight: 1.8 }}>
                    A world where distance doesn't matter - where seamless communication 
                    connects every person, business, and community across the globe.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Values - Centered */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Core Values"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Our Core Values
            </Typography>
            <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}>
              The principles that guide everything we do
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                >
                  <Card sx={{ textAlign: 'center', p: 3, height: '100%', borderRadius: 3 }}>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        bgcolor: value.color + '15',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                        color: value.color,
                      }}
                    >
                      <Box sx={{ fontSize: 35 }}>{value.icon}</Box>
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#191919' }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {value.desc}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Milestones Timeline - Centered */}
      <Box sx={{ py: 10, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Our Journey"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Our Journey
            </Typography>
            <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 6 }}>
              Key milestones in our growth story
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {milestones.map((milestone, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Typography variant="h2" fontWeight="800" sx={{ color: milestone.color, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                      {milestone.year}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#191919', mt: 1 }}>
                      {milestone.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {milestone.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* CTA Section */}
      <CTASection 
        title="Join Our Journey" 
        subtitle="Be part of the global communication revolution"
        buttonText="Work With Us"
        buttonLink="/careers"
      />
    </Box>
  );
};

export default About;